/**
 * chip_launcher_controller.js — the native Chip button steps aside while
 * anything is being filled in (Stimulus, mounted on <body>).
 *
 * Identifier "bridge--chip-launcher". Plain Stimulus, not a BridgeComponent:
 * it sends nothing over Strada, and a BridgeComponent's connect() is UA-gated
 * to iOS, which would leave Android out.
 *
 * The rule, in one place for every runtime: the closed launcher steps aside
 * while a form or sheet is open (Bootstrap's `modal-open` on <body>), a field
 * that raises the keyboard has focus (`data-keyboard-open`, set by
 * keyboard_inset_controller.js), the send prompt's bottom card is up
 * (`data-onboard-prompt`, set by onboard_send_controller.js), the first-run
 * walkthrough's card is up (`data-onboard-walkthrough`, set by
 * walkthrough_controller.js), or the page is the owner's client preview
 * (`data-client-preview`, rendered on <body> by quote_public_view.html). A
 * form's Save button and the card's Send sit in the bottom-right corner where
 * the launcher floats, the walkthrough's card owns the bottom of the screen, a
 * keyboard leaves no room for it, and the client's page has no Chip.
 *
 * In a browser, ai_chat.css applies the rule to the web launcher, except for
 * the walkthrough, whose card's layer simply sits over it. In the apps that
 * launcher is hidden and the shell draws its own button over the web view,
 * which page CSS cannot reach, so this controller tells the shell; under the
 * walkthrough's card the web launcher stands in for it (themes/skin.css). It uses the
 * report the shell already takes from the page for the chat panel ("chatOpen"
 * on iOS, onChatOpenChanged on Android), whose native effect is exactly "hide
 * the button": installed builds pick this up without a store release.
 *
 * The shell's own observer still reports the chat panel opening and closing;
 * this controller only ever adds reasons to hide, and includes the panel's
 * state so closing a form while the panel is open never shows the button.
 */

import { Controller } from "@hotwired/stimulus";

/** Whether the native button should be hidden, given what is on the page. */
export function launcherHidden({
    chatOpen, modalOpen, keyboardOpen, promptOpen = false, clientPreview = false, walkthroughOpen = false,
}) {
    return chatOpen || modalOpen || keyboardOpen || promptOpen || clientPreview || walkthroughOpen;
}

function registerStandalone() {
    window.ChippieBridge.register("chipLauncher", {
        ios: (hidden) => {
            try {
                window.webkit.messageHandlers.chippieShell.postMessage({ type: "chatOpen", open: hidden });
            } catch (_e) { /* no shell handler: nothing to hide */ }
        },
        android: (hidden) => {
            try { window.ChippieNative.onChatOpenChanged(hidden); } catch (_e) { /* bridge gone */ }
        },
        // The browser's launcher is the web one, and ai_chat.css hides it.
        pwa: () => {},
    });
}

// Facade may load after this module; register as soon as both are present.
// globalThis, not window: the module is also imported under Node by its test.
if (globalThis.window?.ChippieBridge) {
    registerStandalone();
} else if (globalThis.document) {
    document.addEventListener("DOMContentLoaded", registerStandalone, { once: true });
}

export default class extends Controller {
    connect() {
        this._lastReported = null;
        this._observer = new MutationObserver(() => this._report());
        this._observer.observe(this.element, {
            attributes: true,
            attributeFilter: ["class", "data-keyboard-open", "data-onboard-prompt", "data-onboard-walkthrough"],
        });
        // The shell marks <html> and then reports the panel ("closed" reads as
        // "show") on every whole page load, over this page's answer. Its mark
        // comes first and this callback after its report, so answering again
        // leaves the page's answer last.
        this._shellObserver = new MutationObserver(() => this._report(true));
        this._shellObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-hotwire-native"] });
        this._report();
    }

    disconnect() {
        this._observer.disconnect();
        this._observer = null;
        this._shellObserver.disconnect();
        this._shellObserver = null;
    }

    // A page without the chat widget never reports: the shell keeps its
    // button hidden until a page says a widget exists, and "not hidden" from
    // here would show a button that opens nothing.
    _report(again = false) {
        const widget = document.querySelector(".pchat--app");
        if (!widget) return;
        const hidden = launcherHidden({
            chatOpen: widget.classList.contains("pchat--open"),
            modalOpen: this.element.classList.contains("modal-open"),
            keyboardOpen: this.element.hasAttribute("data-keyboard-open"),
            promptOpen: this.element.hasAttribute("data-onboard-prompt"),
            walkthroughOpen: this.element.hasAttribute("data-onboard-walkthrough"),
            // Rendered with the page and never toggled, so connect's report covers it.
            clientPreview: this.element.hasAttribute("data-client-preview"),
        });
        if (hidden === this._lastReported && !again) return;
        this._lastReported = hidden;
        window.ChippieBridge?.chipLauncher?.(hidden);
    }
}
